import SelectGroup from "../../../components/FormComponent/SelectGroup";
import useStyles from "../styles";
import {
  Modal as MuiModal,
  IconButton
} from "@mui/material";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import NotFounditem from "../../error/NotFound";
import nosearch from "../../../assets/images/no-search.svg";
import { httprequest } from "../../../data/api";
import PageLoader from "../../../components/PageLoader";
import { toast } from "react-toastify";
import Layout from "../../../components/Layout";
import BreadCrumb from "../../../components/BreadCrumb";
import { useParams } from "react-router-dom";
import useWindowSize from "../../../hooks/useWindow";

// END POINT CALLS
const GET_SESSIONS = "/api/v1/school-plan/session";
const GET_CLASS_GRADES = "/api/v1/session-report/class/";

const SessionBroadsheet = () => {
  const classes = useStyles();
  const { id } = useParams();
  const [activeSession, setActiveSession] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [allSessions, setAllSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [classGrades, setClassGrades] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isPageFetching, setIsPageFetching] = useState(false);
  const [studentDetailsModal, setStudentDetailsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const width = useWindowSize();
  const mobile = width < 600;

  // Get all unique terms from all students' subjects
  const getAllTerms = () => {
    const allTerms = [];
    classGrades?.forEach(student => {
      student?.subjects?.forEach(subject => {
        subject?.terms?.forEach(term => {
          if (!allTerms?.find(t => t?.id === term?.id)) {
            allTerms?.push({
              id: term?.id,
              name: term?.name
            });
          }
        });
      });
    });

    return allTerms;
  };

  /**
 * Fetches all sessions from the server and updates the state.
 */
  const getAllSessions = async () => {
    try {
      const res = await httprequest.get(GET_SESSIONS);
      setAllSessions(res?.data?.data);
      setActiveSession(res?.data?.data?.find((active_session) => active_session?.current_session === true));
    } catch (err) {
      toast.error(err?.response?.message);
    }
  };

  // Handle close Student Details Modal
  const handleCloseStudentDetailsModal = () => {
    setStudentDetailsModal(false);
    setSelectedStudent(null);
  };

  // Open student details modal
  const handleViewStudentDetails = (student) => {
    setSelectedStudent(student);
    setStudentDetailsModal(true);
  };

  const handleSessionChange = (e) => {
    const value = e.target.value;
    setSelectedSession(value);
  };

  // Search function for students
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value?.trim()) {
      setFilteredStudents(classGrades || []);

      return;
    }

    const filtered = classGrades?.filter(student => `${student?.last_name} ${student?.first_name}`?.toLowerCase()?.includes(value) ||
      student?.first_name?.toLowerCase().includes(value) ||
      student?.last_name?.toLowerCase().includes(value)
    );

    setFilteredStudents(filtered);
  };

  /**
 * Fetches all class results for a specific class and term.
 */
  const handleGetAllClassResult = async () => {
    setIsPageFetching(true);
    try {
      const res = await httprequest.get(`${GET_CLASS_GRADES}${id}?session_id=${selectedSession || activeSession?.id}`);
      setIsPageFetching(false);
      setClassGrades(res?.data?.data || []);
      setFilteredStudents(res?.data?.data || []);
    } catch (err) {
      setIsPageFetching(false);
      toast.error(err?.response?.message);
    }
  };

  /**
 * @effect
 * @description On component mount, fetches all sessions, terms, and classes.
 */
  useEffect(() => {
    getAllSessions();
  }, []);

  useEffect(() => {
    if (activeSession?.id && id) {
      handleGetAllClassResult();
    }
  }, [activeSession, selectedSession]);

  // Get all unique terms for column headers
  const allTerms = getAllTerms();

  // Prepare columns for the main table
  const mainTableColumns = [
    {
      name: "Student Name",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Student Name</p>}
          {`${row?.last_name} ${row?.first_name}`}
        </>
      )
    },
    {
      name: "Session Average",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Session Avg</p>}
          {row?.session_average?.toFixed(2) || "--/--"}
        </>
      )
    },
    {
      name: "Session Grade",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Grade</p>}
          {row?.session_grade || "--/--"}
        </>
      )
    },
    {
      name: "",
      cell: (row) => (
        <IconButton
          onClick={() => handleViewStudentDetails(row)}
          className={classes.actionBtn}
        >
          <Icon icon="mingcute:right-line" width="20" height="20" />
        </IconButton>
      )
    }
  ];

  // student details modal (all subjects for a student)
  const studentDetailsColumns = [
    {
      name: "Subject",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Subject</p>}
          {row?.name || "--/--"}
        </>
      )
    },
    // Dynamic term columns based on the selected student's data
    ...(allTerms.map(term => ({
      name: term?.name,
      selector: (row) => {
        const termData = row?.terms?.find(t => t?.id === term?.id);

        return (
          <>
            {mobile && <p className="mobile-styling">{term.name}</p>}
            {termData?.score?.toFixed(2) || "--/--"}
          </>
        );
      }
    }))),
    {
      name: "Term Average",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Term Avg</p>}
          {row?.term_average?.toFixed(2) || "--/--"}
        </>
      )
    },
    {
      name: "Grade",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Grade</p>}
          {row?.grade || "--/--"}
        </>
      )
    }
  ];

  // data for student details modal
  const studentDetailsData = selectedStudent?.subjects || [];

  return (
    <Layout children={
      <>
        <div className="breadcrumb-container">
          <div>
            <BreadCrumb
              link_one="/report/broadsheet"
              link_one_name="Academic Broadsheet"
              active="Session Broadsheet"
              description="See the breakdown of students results in the session"
            />
          </div>
        </div>
        <div className="page-content margin-top-16">
          <div className={classes.headinfo}>
            <div className="flex gap-10 align-items-end margin-top-5">
              <div>
                {activeSession?.id && (
                  <SelectGroup
                    children={
                      <select
                        value={selectedSession || activeSession?.id}
                        onChange={handleSessionChange}
                        name="session"
                      >
                        <option value="">---Select Session---</option>
                        {allSessions?.map((sesh) => {
                          return (
                            <option key={sesh?.id} value={sesh?.id}>
                              {sesh?.session}
                            </option>
                          );
                        })}
                      </select>
                    }
                  />
                )}
              </div>
            </div>
          </div>

          {isPageFetching ? (
            <PageLoader />
          ) : classGrades?.length === 0 ? (
            <div className={classes.notfound}>
              <NotFounditem
                img={nosearch}
                title="No Session BroadSheet Found"
                subtitle="No Broadsheet has been added to the list."
              />
            </div>
          ) : (
            <>
              <div className={`${classes.tableView} ${classes.tableViewActive}`}>
                {filteredStudents?.length === 0 ? (
                  <div className={classes.tableEmpty}>
                    <NotFounditem
                      img={nosearch}
                      title="No Results Found"
                      subtitle="No results have been recorded for this class and session."
                    />
                  </div>
                ) : (
                  <div className={classes.tableContent}>
                    <Table
                      data={filteredStudents}
                      columns={mainTableColumns}
                      pagination
                      subHeaderComponent={
                        <TableSearch
                          placeholder="Search students..."
                          searchTableFunc={handleSearch}
                          value={searchTerm}
                        />
                      }
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Student Details Modal */}
        <MuiModal
          open={studentDetailsModal}
          onClose={handleCloseStudentDetailsModal}
          className={classes.studentModal}
        >
          <div className={classes.studentModalContent}>
            <div className={classes.studentModalHeader}>
              <h3 className={classes.studentModalTitle}>
                {selectedStudent ? `${selectedStudent?.last_name} ${selectedStudent?.first_name}` : ""} - Session Results
              </h3>
              <IconButton
                onClick={handleCloseStudentDetailsModal}
                className={classes.closeButton}
              >
                <Icon icon="mdi:close" width="24" height="24" />
              </IconButton>
            </div>

            <div className={classes.studentModalBody}>
              {studentDetailsData.length > 0 ? (
                <Table
                  data={studentDetailsData}
                  columns={studentDetailsColumns}
                  pagination
                />
              ) : (
                <div className={classes.noData}>
                  No subject results found for this student
                </div>
              )}
            </div>
          </div>
        </MuiModal>
      </>
    }
    />
  );
};

export default SessionBroadsheet;
